<template>
   <v-card-text>
      <!-- ── ALTA Y CORRECCIÓN ────────────────────────────────────────────── -->
      <v-dialog v-model="dialogCuota" max-width="520px" persistent>
         <v-card>
            <v-card-title class="text-h6">
               {{ cuotaSel ? 'Corregir cuota' : 'Agregar cuota' }}
            </v-card-title>
            <v-card-text class="text--primary">
               <p class="text-caption grey--text mb-4">
                  La cuota que registre acá se le aplica a todos los alumnos de
                  esa carrera.
               </p>

               <v-autocomplete
                  v-if="!cuotaSel"
                  v-model="formCarrera"
                  :items="carreras"
                  :item-text="etiquetaCarrera"
                  item-value="integracionCarreraRaId"
                  label="Carrera"
                  return-object
                  outlined
                  dense
                  class="mb-3"
                  hide-details
                  no-data-text="Registro Académico todavía no ha compartido ninguna carrera."
                  @change="alElegirCarrera"
               />
               <p v-if="!cuotaSel" class="text-caption grey--text mt-1 mb-3">
                  Las carreras son las que comparte Registro Académico. Al
                  elegir una, la sede se toma de ese mismo catálogo.
               </p>
               <p class="text-caption grey--text mb-3">
                  Sede: {{ cuotaSel ? cuotaSel.sede : sedeElegida || '—' }}
                  <span v-if="cuotaSel">
                     · Código {{ cuotaSel.carreraCodigo }}
                  </span>
               </p>

               <!-- Este es el nombre que sale en el Excel, el PDF y el Word. -->
               <v-text-field
                  v-if="cuotaSel || formCarrera"
                  v-model="formNombre"
                  label="Nombre de la carrera en los documentos"
                  hint="Revise que lleve las tildes: así aparecerá en el listado oficial y en las cartas."
                  persistent-hint
                  outlined
                  dense
                  class="mb-3"
               />

               <v-alert
                  v-if="!cuotaSel && formCarrera && formCarrera.sinResolver"
                  dense
                  text
                  type="warning"
                  class="text-body-2"
               >
                  Esta carrera todavía no se ha podido relacionar con los
                  contratos, así que la cuota que registre no se le aplicará a
                  ningún alumno. Puede guardarla igual: en cuanto haya contratos
                  de esa carrera, la relación se resuelve sola.
               </v-alert>

               <v-text-field
                  v-model.number="formCuota"
                  label="Cuota mensual UNICAES"
                  prefix="$"
                  type="number"
                  outlined
                  dense
                  hide-details
               />
            </v-card-text>
            <v-card-actions>
               <v-spacer />
               <v-btn text @click="dialogCuota = false">Cancelar</v-btn>
               <v-btn
                  color="primary"
                  :loading="guardando"
                  @click="guardarCuota"
               >
                  Guardar
               </v-btn>
            </v-card-actions>
         </v-card>
      </v-dialog>

      <!-- ── BITÁCORA DE CARRERAS ─────────────────────────────────────────── -->
      <v-dialog v-model="dialogBitacora" max-width="820px" scrollable>
         <v-card>
            <v-card-title class="text-h6">
               Bitácora de carreras de Registro Académico
            </v-card-title>
            <v-card-text class="text--primary" style="max-height: 480px">
               <p class="text-caption grey--text mb-3">
                  Aquí queda registrado cada cambio que Registro Académico hace
                  en su lista de carreras.
               </p>

               <v-data-table
                  :headers="headersBitacora"
                  :items="bitacora"
                  :items-per-page="10"
                  :footer-props="pieTabla"
                  dense
                  class="elevation-1"
                  no-data-text="Todavía no se ha registrado ningún cambio."
               >
                  <template #[`item.etiquetaEvento`]="{ item }">
                     <v-chip
                        x-small
                        label
                        :color="colorEvento(item.evento)"
                        text-color="white"
                     >
                        {{ item.etiquetaEvento }}
                     </v-chip>
                  </template>
               </v-data-table>
            </v-card-text>
            <v-card-actions>
               <v-spacer />
               <v-btn text @click="dialogBitacora = false">Cerrar</v-btn>
            </v-card-actions>
         </v-card>
      </v-dialog>

      <!-- ── PRECARGA DESDE CONTRATOS ─────────────────────────────────────── -->
      <v-dialog v-model="dialogPrecarga" max-width="640px" persistent>
         <v-card>
            <v-card-title class="text-h6">
               Calcular cuotas desde los talonarios.
            </v-card-title>
            <v-card-text class="text--primary">
               <v-alert
                  dense
                  icon="fas fa-info-circle"
                  class="aviso-dorado mb-3"
               >
                  Se deduce la cuota de cada carrera desde los talonarios
                  del ciclo <BR/><strong>{{ ciclo || '—' }}</strong
                  >
               </v-alert>

               <v-checkbox
                  v-model="pisarExistentes"
                  dense
                  hide-details
                  class="mt-0 mb-2"
                  label="Recalcular también las carreras que ya tienen monto"
               />

               <p class="text-caption grey--text mb-0">
                  <template v-if="pisarExistentes">
                     Se va a recalcular el precio de todas las carreras,
                     incluidas las que alguien haya escrito a mano.
                  </template>
                  <template v-else>
                     Si es su primera vez y desea el cálculo de todas las cuotas, marque el recuadro; si el recuadro está en blanco, solo se calcularán las cuotas que no posean cuota.
                  </template>
               </p>
            </v-card-text>
            <v-card-actions>
               <v-spacer />
               <v-btn text @click="dialogPrecarga = false">Cancelar</v-btn>
               <v-btn
                  color="primary"
                  :loading="precargando"
                  :disabled="!ciclo"
                  @click="confirmarPrecarga"
               >
                  Calcular
               </v-btn>
            </v-card-actions>
         </v-card>
      </v-dialog>

      <!-- ── CONVENIO ─────────────────────────────────────────────────────── -->
      <v-dialog v-model="dialogConvenio" max-width="520px">
         <v-card>
            <v-card-title class="text-h6">
               Porcentajes del convenio
            </v-card-title>
            <v-card-text class="text--primary">
               <v-alert
                  dense
                  icon="fas fa-info-circle"
                  class="aviso-dorado mb-4"
               >
                  El aporte de Integración es el que se imprime en el listado
                  oficial.
               </v-alert>
               <v-row dense>
                  <v-col cols="6">
                     <v-text-field
                        v-model.number="porcentajeIntegracion"
                        label="Aporte Integración"
                        suffix="%"
                        type="number"
                        outlined
                        dense
                        hide-details
                     />
                  </v-col>
                  <v-col cols="6">
                     <v-text-field
                        v-model.number="porcentajeUniversidad"
                        label="Aporte universidad"
                        suffix="%"
                        type="number"
                        outlined
                        dense
                        hide-details
                     />
                  </v-col>
               </v-row>
            </v-card-text>
            <v-card-actions>
               <v-spacer />
               <v-btn text @click="dialogConvenio = false">Cancelar</v-btn>
               <v-btn color="primary" @click="guardarConvenio">Guardar</v-btn>
            </v-card-actions>
         </v-card>
      </v-dialog>

      <!-- ── BAJA ─────────────────────────────────────────────────────────── -->
      <v-dialog v-model="dialogEliminar" max-width="460px">
         <v-card>
            <v-card-title class="text-h6">Quitar la cuota</v-card-title>
            <v-card-text class="text--primary">
               ¿Quita la cuota de
               <strong>{{ cuotaSel ? cuotaSel.carreraNombre : '' }}</strong> en
               {{ cuotaSel ? cuotaSel.sede : '' }}? Los cortes que ya se
               generaron no cambian, porque cada uno guarda el monto con el que
               se emitió; esto solo afecta a los que se generen de ahora en
               adelante.
            </v-card-text>
            <v-card-actions>
               <v-spacer />
               <v-btn text @click="dialogEliminar = false">Cancelar</v-btn>
               <v-btn color="error" text @click="confirmarEliminar">
                  Quitar
               </v-btn>
            </v-card-actions>
         </v-card>
      </v-dialog>

      <!-- ── BARRA ────────────────────────────────────────────────────────── -->
      <v-row dense class="mb-2">
         <v-col cols="12" class="d-flex align-center flex-wrap">
            <v-btn
               v-if="permController(34, 'crear')"
               color="primary"
               outlined
               class="mr-2 mb-1"
               @click="abrirCuota(null)"
            >
               <v-icon left small>fas fa-plus</v-icon>
               Agregar cuota
            </v-btn>

            <v-btn
               v-if="permController(34, 'crear')"
               color="primary"
               text
               class="mr-2 mb-1"
               @click="abrirPrecarga"
            >
               <v-icon left small>fas fa-calculator</v-icon>
               Calcular cuotas
            </v-btn>

            <v-btn
               v-if="permController(34, 'editar')"
               color="primary"
               text
               class="mr-2 mb-1"
               @click="dialogConvenio = true"
            >
               <v-icon left small>fas fa-percent</v-icon>
               Convenio
               <span v-if="convenio" class="ml-1">
                  ({{ convenio.porcentajeIntegracion }}%)
               </span>
            </v-btn>

            <v-btn text small class="mr-2 mb-1" @click="verBitacora">
               <v-icon left x-small>fas fa-history</v-icon>
               Bitácora de carreras
            </v-btn>

            <div style="max-width: 260px" class="mr-3 mb-1">
               <!-- Autocomplete y no select: son treinta y pico de carreras y
                    buscar escribiendo es más rápido. -->
               <v-autocomplete
                  v-model="filtroCarrera"
                  :items="carrerasEnCuotas"
                  label="Carrera"
                  outlined
                  dense
                  hide-details
                  clearable
                  no-data-text="Sin carreras"
               />
            </div>

            <v-spacer />

            <v-chip label color="primary" outlined class="mb-1">
               {{ cuotasMostradas.length }} de {{ cuotas.length }} cuota(s)
            </v-chip>
         </v-col>
      </v-row>

      <!-- Apartado de cuotas sin monto: lo primero que hay que resolver, así que
           va destacado y con su propio acceso para verlas. -->
      <v-card v-if="pendientes.length" outlined class="mb-3 tarjeta-pendientes">
         <v-card-text class="d-flex align-center flex-wrap py-3">
            <v-icon color="orange darken-2" class="mr-3" large>
               fas fa-exclamation-circle
            </v-icon>

            <div class="mr-4">
               <div
                  class="text-h5 font-weight-bold orange--text text--darken-3"
               >
                  {{ pendientes.length }}
               </div>
               <div class="text-caption grey--text text--darken-1">
                  de {{ cuotas.length }} carrera(s) sin monto
               </div>
            </div>

            <div class="text-body-2" style="max-width: 560px">
               Estas carreras están registradas pero todavía no tienen la cuota
               asignada. Mientras sigan en cero no se les puede poner precio a
               los alumnos: al generar el corte se reportan como faltantes en
               lugar de cobrarse en cero.
               <strong>Indíqueles el monto que corresponde a cada una.</strong>
            </div>

            <v-spacer />

            <v-btn
               small
               :color="soloPendientes ? 'orange darken-2' : 'primary'"
               :outlined="!soloPendientes"
               :dark="soloPendientes"
               class="mb-1"
               @click="soloPendientes = !soloPendientes"
            >
               <v-icon left x-small>
                  {{ soloPendientes ? 'fas fa-list' : 'fas fa-filter' }}
               </v-icon>
               {{ soloPendientes ? 'Ver todas' : 'Ver solo las que faltan' }}
            </v-btn>
         </v-card-text>
      </v-card>

      <!-- Carreras que Registro Académico agregó y nadie ha revisado. -->
      <v-alert
         v-if="avisoCarreras.nuevas"
         dense
         dark
         color="#6C1C19"
         icon="fas fa-info-circle"
         class="mb-2 text-body-2"
      >
         <div class="d-flex align-center flex-wrap">
            <span class="mr-2">{{ avisoCarreras.mensaje }}</span>
            <v-spacer />
            <v-btn x-small text class="mr-1" @click="verBitacora">
               Ver bitácora
            </v-btn>
            <v-btn x-small text @click="marcarCarrerasVistas">Entendido</v-btn>
         </div>
      </v-alert>

      <v-alert
         v-if="detallePrecarga.length"
         dense
         outlined
         color="grey darken-4"
         class="mb-3 text-body-2"
      >
         <div class="font-weight-medium mb-1">
            Revise estas carreras después de la precarga
         </div>
         <ul class="mb-1">
            <li v-for="(d, i) in detallePrecarga" :key="i">{{ d }}</li>
         </ul>
         <v-btn x-small text class="mt-1" @click="detallePrecarga = []">
            Cerrar
         </v-btn>
      </v-alert>

      <v-data-table
         :headers="headers"
         :items="cuotasMostradas"
         :loading="cargando"
         :footer-props="pieTabla"
         :items-per-page="25"
         dense
         class="elevation-1"
         no-data-text="Todavía no hay cuotas registradas. Las carreras aparecen solas al cargar el listado del mes."
      >
         <template #[`item.cuotaUnicaes`]="{ item }">
            <v-chip
               v-if="!item.cuotaUnicaes"
               x-small
               label
               color="orange"
               text-color="white"
            >
               pendiente
            </v-chip>
            <strong v-else>{{ moneda(item.cuotaUnicaes) }}</strong>
         </template>
         <template #[`item.montoIntegracion`]="{ item }">
            <span v-if="!item.cuotaUnicaes" class="grey--text">—</span>
            <span v-else>{{ moneda(item.montoIntegracion) }}</span>
         </template>
         <template #[`item.acciones`]="{ item }">
            <v-tooltip v-if="permController(34, 'editar')" bottom>
               <template #activator="{ on }">
                  <v-icon
                     small
                     color="primary"
                     class="mr-2"
                     v-on="on"
                     @click="abrirCuota(item)"
                  >
                     fas fa-pen
                  </v-icon>
               </template>
               <span>Corregir la cuota</span>
            </v-tooltip>
            <v-tooltip v-if="permController(34, 'eliminar')" bottom>
               <template #activator="{ on }">
                  <v-icon
                     small
                     color="error"
                     v-on="on"
                     @click="abrirEliminar(item)"
                  >
                     fas fa-trash
                  </v-icon>
               </template>
               <span>Quitar la cuota</span>
            </v-tooltip>
         </template>
      </v-data-table>
   </v-card-text>
</template>

<script>
import axios from 'axios'
import { authComputed } from '../../helpers'

/**
 * Catálogo de cuotas: una por carrera y sede.
 *
 * Es la ÚNICA fuente de precio del listado oficial. Cada corte mensual toma de
 * acá lo que le corresponde a cada alumno, sin importar en qué año ingresó ni
 * de qué mes sea el corte: paga lo que diga su carrera.
 *
 * Antes el precio se deducía de los contratos reales, mes a mes, en una pantalla
 * aparte. Ese camino quedó fuera de uso.
 */
export default {
   name: 'CuotasIntegracion',
   props: {
      ciclo: {
         type: String,
         default: null
      }
   },
   data() {
      return {
         cuotas: [],
         carreras: [],
         cargando: false,
         guardando: false,
         migrando: false,

         dialogCuota: false,
         cuotaSel: null,
         formCarrera: null,
         formSede: null,
         formNombre: '',
         formCuota: 0,

         // Solo las carreras a las que todavía no se les puso monto.
         soloPendientes: false,

         avisoCarreras: {},
         bitacora: [],
         dialogBitacora: false,

         dialogEliminar: false,

         // Los dos filtros que antes vivían en la pantalla de cuotas del mes.
         filtroCarrera: null,

         dialogPrecarga: false,
         precargando: false,
         pisarExistentes: false,
         detallePrecarga: [],

         dialogConvenio: false,
         convenio: null,
         porcentajeIntegracion: 80,
         porcentajeUniversidad: 30,

         // Vuetify viene en inglés y el proyecto no configura idioma global.
         pieTabla: {
            'items-per-page-text': 'Filas por página',
            'items-per-page-options': [10, 25, 50, 100, -1],
            'items-per-page-all-text': 'Todas',
            'page-text': '{0}-{1} de {2}'
         },

         headersBitacora: [
            { text: 'Cuándo', value: 'fechaEvento' },
            { text: 'Qué pasó', value: 'etiquetaEvento' },
            { text: 'Carrera', value: 'carreraNombre' }
         ],

         headers: [
            { text: 'Carrera', value: 'carreraNombre' },
            { text: 'Código', value: 'carreraCodigo' },
            { text: 'Cuota UNICAES', value: 'cuotaUnicaes' },
            { text: 'Aporte D.I.', value: 'montoIntegracion' },
            { text: 'Modificada', value: 'modificadaEl' },
            { text: '', value: 'acciones', sortable: false }
         ]
      }
   },
   computed: {
      ...authComputed,
      /** Carreras presentes en las cuotas, para el filtro. */
      carrerasEnCuotas() {
         return [...new Set(this.cuotas.map((c) => c.carreraNombre))]
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b, 'es'))
      },
      /** Carreras sembradas a las que todavía nadie les puso monto. */
      pendientes() {
         return this.cuotas.filter((c) => !c.cuotaUnicaes)
      },
      /** Lo que ve la tabla, con los filtros y el modo «solo pendientes». */
      cuotasMostradas() {
         const base = this.soloPendientes ? this.pendientes : this.cuotas

         return base.filter((c) => {
            if (this.filtroCarrera && c.carreraNombre !== this.filtroCarrera) {
               return false
            }
            return true
         })
      },
      /** La sede sale del catálogo, junto con la carrera elegida. */
      sedeElegida() {
         return this.formCarrera ? this.formCarrera.sede : null
      },
      usuario() {
         return this.oidcUser ? this.oidcUser.name : 'Desconocido'
      }
   },
   watch: {
      ciclo: {
         immediate: true,
         handler() {
            this.cargarCarreras()
            this.cargarConvenio()
            this.listar()
         }
      }
   },
   methods: {
      moneda(valor) {
         return `$${Number(valor || 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
         })}`
      },
      avisar(texto, color = 'success') {
         this.$emit('mensaje', texto, color)
      },
      /**
       * La tabla de alumnos muestra la cuota y el aporte de cada becario, que
       * salen de acá. Cada vez que estas cifras cambian hay que avisarle, o
       * queda mostrando los valores viejos.
       */
      avisarCambio() {
         this.$emit('cuotas-cambiadas')
      },
      cargarConvenio() {
         if (!this.ciclo) return

         axios
            .get('api/Integracion/convenio', { params: { ciclo: this.ciclo } })
            .then(({ data }) => {
               this.convenio = data
               this.porcentajeIntegracion = data.porcentajeIntegracion
               this.porcentajeUniversidad = data.porcentajeUniversidad
            })
            .catch(() => {
               // Sin convenio configurado todavía: se deja el 80/30 por defecto
               // para que el diálogo abra con algo sensato.
               this.convenio = null
            })
      },
      guardarConvenio() {
         axios
            .put('api/Integracion/convenio', {
               ciclo: this.ciclo,
               porcentajeIntegracion: this.porcentajeIntegracion,
               porcentajeUniversidad: this.porcentajeUniversidad,
               usuario: this.usuario
            })
            .then(() => {
               this.dialogConvenio = false
               this.avisar('Convenio guardado.')
               this.cargarConvenio()
               this.listar()
               this.avisarCambio()
            })
            .catch((error) => {
               this.avisar(
                  this.mensajeError(error, 'No se pudo guardar el convenio.'),
                  'error'
               )
            })
      },
      mensajeError(error, respaldo) {
         return error.response && error.response.data
            ? error.response.data
            : respaldo
      },

      /**
       * El catálogo de carreras sale de la vista de Registro Académico, no de
       * los contratos: es ese nombre el que llega después en el listado, así que
       * es el que tiene que quedar guardado en la cuota para que cruce.
       *
       * La misma consulta devuelve el aviso de las carreras que ellos agregaron
       * y nadie ha revisado.
       */
      cargarCarreras() {
         axios
            .get('api/Integracion/carreras-ra', {
               params: { ciclo: this.ciclo }
            })
            .then(({ data }) => {
               this.carreras = (data && data.carreras) || []
               this.avisoCarreras = data || {}
            })
            .catch((error) => console.log(error))
      },
      /** «CARRERA (SEDE)»: la misma carrera existe en las dos sedes. */
      etiquetaCarrera(carrera) {
         return `${carrera.carreraNombre} (${carrera.sede})`
      },
      /** La sede no se elige: viene con la carrera del catálogo. */
      alElegirCarrera(carrera) {
         this.formSede = carrera ? carrera.sedeCodigo : null
         this.formNombre = carrera ? carrera.carreraNombre : ''
      },
      /**
       * Se pregunta antes de calcular: la precarga puede tirar abajo precios
       * escritos a mano, y eso no debería pasar de callado.
       */
      abrirPrecarga() {
         this.pisarExistentes = false
         this.dialogPrecarga = true
      },
      confirmarPrecarga() {
         this.precargando = true

         axios
            .post('api/Integracion/cuotas/precargar-desde-contratos', {
               ciclo: this.ciclo,
               pisarExistentes: this.pisarExistentes,
               usuario: this.usuario
            })
            .then(({ data }) => {
               this.dialogPrecarga = false
               this.resumenPrecarga(data)
               this.listar()
               this.avisarCambio()
            })
            .catch((error) => {
               this.avisar(
                  this.mensajeError(
                     error,
                     'No se pudieron precargar las cuotas.'
                  ),
                  'error'
               )
            })
            .finally(() => {
               this.precargando = false
            })
      },
      /** Un solo aviso con lo que importa, y el detalle en el panel de abajo. */
      resumenPrecarga(data) {
         // El detalle va al panel y no al aviso: son varias líneas y en el
         // mensaje emergente quedaban cortadas.
         this.detallePrecarga = (data.sinContratos || [])
            .map(
               (c) =>
                  `${c}: no se encontraron contratos de los que deducir el precio`
            )
            .concat(data.requierenRevision || [])

         if (data.aviso) {
            this.avisar(data.aviso, 'warning')
            return
         }

         const partes = [`${data.escritas} cuota(s) calculadas`]

         if (data.conservadas) {
            partes.push(`${data.conservadas} con monto que se respetaron`)
         }
         if (data.sinContratos && data.sinContratos.length) {
            partes.push(`${data.sinContratos.length} sin contratos`)
         }

         this.avisar(partes.join(' · '))
      },
      verBitacora() {
         axios
            .get('api/Integracion/carreras-ra/bitacora')
            .then(({ data }) => {
               this.bitacora = data || []
               this.dialogBitacora = true
            })
            .catch((error) => {
               this.avisar(
                  this.mensajeError(error, 'No se pudo leer la bitácora.'),
                  'error'
               )
            })
      },
      /**
       * Deja de avisar sobre las carreras nuevas. No borra nada: la bitácora
       * conserva cuándo apareció cada una.
       */
      marcarCarrerasVistas() {
         axios
            .post('api/Integracion/carreras-ra/marcar-vistas', null, {
               params: { usuario: this.usuario }
            })
            .then(() => {
               this.avisoCarreras = { ...this.avisoCarreras, nuevas: 0 }
            })
            .catch((error) => console.log(error))
      },
      colorEvento(evento) {
         if (evento === 'NUEVA') return 'green darken-2'
         if (evento === 'CODIGO_CAMBIADO') return 'orange darken-2'
         if (evento === 'RETIRADA') return 'red darken-2'
         return 'blue-grey'
      },
      listar() {
         this.cargando = true

         axios
            .get('api/Integracion/cuotas', { params: { ciclo: this.ciclo } })
            .then(({ data }) => {
               this.cuotas = data || []
            })
            .catch((error) => console.log(error))
            .finally(() => {
               this.cargando = false
            })
      },

      abrirCuota(item) {
         this.cuotaSel = item
         this.formCarrera = null
         this.formSede = null
         this.formNombre = item ? item.carreraNombre : ''
         this.formCuota = item ? item.cuotaUnicaes : 0
         this.dialogCuota = true
      },
      guardarCuota() {
         const codigo = this.cuotaSel
            ? this.cuotaSel.carreraCodigo
            : this.formCarrera && this.formCarrera.carreraCodigo
         const nombre = (this.formNombre || '').trim()
         const sede = this.cuotaSel ? this.cuotaSel.sede : this.formSede

         if (!this.cuotaSel && !this.formCarrera) {
            this.avisar('Elija la carrera.', 'error')
            return
         }
         if (!nombre) {
            this.avisar('Escriba el nombre de la carrera.', 'error')
            return
         }
         if (!codigo) {
            this.avisar(
               'Esa carrera todavía no se ha podido relacionar con los contratos, ' +
                  'así que aún no se le puede registrar la cuota.',
               'error'
            )
            return
         }
         if (!this.formCuota || this.formCuota <= 0) {
            this.avisar('La cuota debe ser mayor que cero.', 'error')
            return
         }

         this.guardando = true

         axios
            .post('api/Integracion/cuotas', {
               carreraCodigo: codigo,
               carreraNombre: nombre,
               sede,
               cuotaUnicaes: this.formCuota,
               usuario: this.usuario
            })
            .then(() => {
               this.dialogCuota = false
               this.avisar('Cuota guardada.')
               this.listar()
               this.avisarCambio()
            })
            .catch((error) => {
               this.avisar(
                  this.mensajeError(error, 'No se pudo guardar la cuota.'),
                  'error'
               )
            })
            .finally(() => {
               this.guardando = false
            })
      },

      abrirEliminar(item) {
         this.cuotaSel = item
         this.dialogEliminar = true
      },
      confirmarEliminar() {
         axios
            .delete(
               `api/Integracion/cuotas/${this.cuotaSel.integracionCuotaId}`,
               { params: { usuario: this.usuario } }
            )
            .then(() => {
               this.dialogEliminar = false
               this.avisar('Cuota quitada.')
               this.listar()
               this.avisarCambio()
            })
            .catch((error) => {
               this.avisar(
                  this.mensajeError(error, 'No se pudo quitar la cuota.'),
                  'error'
               )
            })
      }
   }
}
</script>

<style scoped>
/* El apartado de cuotas sin monto: contorno naranja para que se distinga del
   resto de la pantalla sin gritar como una alerta de error. */
.tarjeta-pendientes {
   border: 1px solid #ef6c00 !important;
   background-color: #fff8e1 !important;
}

/* Aviso informativo: contorno dorado, fondo blanco y letra negra, el mismo del
   resto del módulo. */
.aviso-dorado {
   background-color: #ffffff !important;
   border: 1px solid #c6ab55 !important;
   color: #000000 !important;
}
</style>
